import { isEmpty } from 'lodash'
import { action, computed, observable, makeObservable } from 'mobx'
import {
  DO_NOT_SERIALIZE,
  FilterItem,
  FilterItemSource,
  IFilterViewModel,
} from '../../FiltersViewModel'
import { task } from 'mobx-task/lib/task'

export class SelectFilter<TKey, TValue>
  implements IFilterViewModel<TKey, TValue[]>
{
  key: TKey
  title: string
  @observable editing = false
  @observable searchText = ''
  @observable multi = false
  @observable selected: TValue[] = []
  @observable options: FilterItem<TValue>[] = []
  itemSource?: FilterItemSource<TValue>

  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  constructor(
    key: TKey,
    title: string,
    itemSource?: FilterItemSource<TValue>,
    multi?: boolean,
    initialValue?: TValue[],
  ) {
    makeObservable(this)
    this.key = key
    this.title = title
    this.multi = multi ?? false
    this.selected = initialValue ?? []
    this.options = []
    this.itemSource = itemSource
  }

  loadOptions = task(async () => {
    if (this.itemSource) {
      this.setOptions(await this.itemSource())
    }
  })

  async activate() {
    await this.loadOptions()
  }

  toString(limit?: number) {
    let items = this.selected.map((v) => this.optionsMap.get(v))

    // If a limit was set truncate the list to show items, +n selected.
    if (limit && items.length > limit) {
      items = items.slice(0, limit - 1)

      const otherItemsCount = this.selected.length - items.length
      items.push(`${limit > 1 ? '+' : ''}${otherItemsCount} selected`)
    }

    return items.join(', ')
  }

  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  @action.bound
  changeSearchText(text: string) {
    this.searchText = text
  }

  @computed
  get value() {
    return this.selected
  }

  @computed
  get searchFilterOptions() {
    if (this.searchText.length === 0) {
      return this.options.slice()
    }

    return this.options.filter(
      (option) =>
        option.title.toLowerCase().includes(this.searchText.toLowerCase()) ||
        option.description
          ?.toLowerCase()
          .includes(this.searchText.toLowerCase()),
    )
  }

  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  @action.bound
  change(val: TValue[]) {
    this.selected = val
  }

  @action.bound
  clearValue() {
    this.selected = []
  }

  @action.bound
  toggle(val: TValue) {
    if (this.multi) {
      this.change(
        this.isSelected(val)
          ? this.selected.filter((s) => s !== val)
          : [...this.selected, val],
      )
      return
    }

    if (!this.isSelected(val)) {
      this.selected = [val]
    }
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  isSelected(val: TValue) {
    return this.selected.some((s) => s === val)
  }

  serialize() {
    return (
      JSON.stringify(
        this.options.filter((o) => this.selected.includes(o.value)),
      ) || DO_NOT_SERIALIZE
    )
  }

  deserialize(value: unknown) {
    /**
     * May behave unexpectedly if existing enum values change
     */
    try {
      if (typeof value === 'string') {
        this.options = JSON.parse(value || '{}')
        this.selected = this.options.map((o) => o.value)
      }
    } catch (error) {
      this.clearValue()
    }
  }
}
